import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, Bot, Cpu, Plus, Search, Wrench } from 'lucide-react';
import { llmGatewayApi } from '../../llm-gateway/llmGatewayApi';
import type { LlmGateway } from '../../llm-gateway/llmGatewayTypes';
import { clientTypeLabel } from '../governedMcpConstants';
import QuotaAssignDrawer from '../drawers/QuotaAssignDrawer';
import QuotaScopeDrawer from '../drawers/QuotaScopeDrawer';
import QuotaUserDrawer from '../drawers/QuotaUserDrawer';
import EmptyState from '../components/EmptyState';
import { ClientStatusBadge } from '../components/StatusBadges';
import { TablePager, fieldClass, ghostBtn, initials, pageSlice, primaryBtn, selectField, tableHeadClass, tdClass, thClass, trClass } from '../components/GatewayUi';
import { logGovernedMcp } from '../governedMcpLogger';
import { useGovernedMcp } from '../governedMcpStore';
import type {
  ClientStatus,
  ClientType,
  McpCapabilityKind,
  PublishedCapability,
  QuotaAssignmentStatus,
  QuotaKind,
  QuotaListingTab,
  QuotaScope,
  QuotaUser,
  QuotaUserStatus,
} from '../governedMcpTypes';
import {
  buildAssignedQuotaUsers,
  buildEffectiveQuotaUsers,
  clientLabel,
  currentQuotaMonth,
  departmentMemberIds,
  filterQuotaUsers,
  findQuotaAssignment,
  formatQuotaMonth,
  inferQuotaResourceType,
  isQuotaMonth,
  formatTokens,
  remainingTokens,
  quotaStatus,
  sourceLabel,
  utilizationPercent,
} from '../quotaUsage';

type UtilFilter = 'all' | QuotaUserStatus;

const QUOTA_KIND_TABS: { id: QuotaKind; label: string; purpose: string; icon: React.ComponentType<{ size?: number }> }[] = [
  { id: 'client', label: 'Client', purpose: 'Control consumption by applications, copilots, or clients.', icon: Bot },
  { id: 'tool', label: 'Tools', purpose: 'Control tool execution and external API usage.', icon: Wrench },
  { id: 'llm', label: 'LLM Gateway', purpose: 'Control model inference through the gateway.', icon: Cpu },
];

const SCOPE_FILTERS: { id: Exclude<QuotaListingTab, 'client'>; label: string }[] = [
  { id: 'user', label: 'User' },
  { id: 'role', label: 'Role' },
  { id: 'department', label: 'Department' },
];

function capabilityOf(item: PublishedCapability): McpCapabilityKind {
  if (item.mcpCapability) return item.mcpCapability;
  const hasTool = item.mcpRepresentations?.includes('tool');
  const hasResource = item.mcpRepresentations?.includes('resource');
  if (hasResource && !hasTool) return 'resource';
  return 'tool';
}

function statusLabel(status: QuotaUserStatus): string {
  if (status === 'exceeded') return 'Over limit';
  if (status === 'warning') return 'High';
  return 'On track';
}

function barTone(status: QuotaUserStatus, isLight: boolean): string {
  if (status === 'exceeded') return isLight ? 'bg-rose-500' : 'bg-rose-400';
  if (status === 'warning') return isLight ? 'bg-amber-500' : 'bg-amber-400';
  return isLight ? 'bg-indigo-500' : 'bg-indigo-400';
}

function pillTone(status: QuotaUserStatus, isLight: boolean): string {
  if (status === 'exceeded') return isLight ? 'bg-rose-50 text-rose-700' : 'bg-rose-500/15 text-rose-300';
  if (status === 'warning') return isLight ? 'bg-amber-50 text-amber-700' : 'bg-amber-500/15 text-amber-300';
  return isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/15 text-emerald-300';
}

function assignmentStatusTone(status: QuotaAssignmentStatus, isLight: boolean): string {
  if (status === 'inactive') return isLight ? 'bg-neutral-100 text-neutral-500' : 'bg-neutral-800 text-neutral-400';
  return isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/15 text-emerald-300';
}

function recordStatusTone(status: string, isLight: boolean): string {
  if (status === 'active' || status === 'published') return isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/15 text-emerald-300';
  if (status === 'disabled' || status === 'revoked') return isLight ? 'bg-rose-50 text-rose-700' : 'bg-rose-500/15 text-rose-300';
  return isLight ? 'bg-neutral-100 text-neutral-500' : 'bg-neutral-800 text-neutral-400';
}

function sourceTone(source: string, isLight: boolean): string {
  if (source === 'User') return isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-300';
  if (source === 'Role') return isLight ? 'bg-sky-50 text-sky-700' : 'bg-sky-500/15 text-sky-300';
  if (source === 'Department') return isLight ? 'bg-violet-50 text-violet-700' : 'bg-violet-500/15 text-violet-300';
  return isLight ? 'bg-neutral-100 text-neutral-500' : 'bg-neutral-800 text-neutral-400';
}

function typeTabClass(active: boolean, isLight: boolean): string {
  return `inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
    active
      ? isLight
        ? 'bg-neutral-900 text-white'
        : 'bg-white text-neutral-900'
      : isLight
        ? 'text-neutral-500 hover:text-neutral-800'
        : 'text-neutral-400 hover:text-white'
  }`;
}

type ScopeRow = {
  scope: QuotaScope;
  scopeId: string;
  name: string;
  clientId: string;
  assignedLimit: number | null;
  status: QuotaAssignmentStatus | null;
  users: number;
  used: number;
  remaining: number;
  tokenLimit: number;
};

type ClientRow = {
  id: string;
  name: string;
  clientType: ClientType;
  registrationId: string;
  status: ClientStatus;
  assignedLimit: number;
  used: number;
  remaining: number;
  tokenLimit: number;
};

export default function QuotasSpendingsTab({
  isLight,
  onToast,
}: {
  isLight: boolean;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { state, quotaDirectory } = useGovernedMcp();
  const [quotaKind, setQuotaKind] = useState<QuotaKind>('client');
  const [listing, setListing] = useState<QuotaListingTab>('user');
  const [query, setQuery] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState(currentQuotaMonth());
  const [utilFilter, setUtilFilter] = useState<UtilFilter>('all');
  const [page, setPage] = useState(1);
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<QuotaUser | null>(null);
  const [selectedScope, setSelectedScope] = useState<{ scope: QuotaScope; scopeId: string; name: string; clientId: string } | null>(
    null
  );
  const [gateways, setGateways] = useState<LlmGateway[]>([]);
  const [gatewaysLoading, setGatewaysLoading] = useState(false);
  const [gatewaysError, setGatewaysError] = useState<string | null>(null);

  const selectedKind = QUOTA_KIND_TABS.find((item) => item.id === quotaKind) ?? QUOTA_KIND_TABS[0];
  const clients = state.clients;

  useEffect(() => {
    if (!clients.length) return;
    if (!clientFilter || !clients.some((client) => client.id === clientFilter)) {
      setClientFilter(clients[0].id);
    }
  }, [clientFilter, clients]);

  useEffect(() => {
    let cancelled = false;
    setGatewaysLoading(true);
    setGatewaysError(null);
    logGovernedMcp('app', 'quota_listing_gateways_load');
    llmGatewayApi
      .listGateways()
      .then((items) => {
        if (cancelled) return;
        setGateways(items);
        logGovernedMcp('app', 'quota_listing_gateways_loaded', { count: items.length });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unable to load LLM gateways.';
        setGatewaysError(message);
        logGovernedMcp('error', 'quota_listing_gateways_failed', { message });
      })
      .finally(() => {
        if (!cancelled) setGatewaysLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const registeredClientIds = useMemo(() => new Set(clients.map((item) => item.id)), [clients]);
  const monthUsers = useMemo(
    () =>
      buildEffectiveQuotaUsers(state.apiKeys, state.quotaAssignments, state.quotaUsages, quotaDirectory, monthFilter),
    [monthFilter, quotaDirectory, state.apiKeys, state.quotaAssignments, state.quotaUsages]
  );
  const users = useMemo(
    () =>
      buildAssignedQuotaUsers(
        state.quotaAssignments,
        state.quotaUsages,
        quotaDirectory,
        monthFilter,
        registeredClientIds
      ),
    [monthFilter, quotaDirectory, registeredClientIds, state.quotaAssignments, state.quotaUsages]
  );

  const clientRows = useMemo<ClientRow[]>(
    () =>
      clients.map((client) => {
        const members = filterQuotaUsers(monthUsers, client.id);
        const tokenLimit = members.reduce((sum, user) => sum + user.tokenLimit, 0);
        const used = members.reduce((sum, user) => sum + user.tokensUsed, 0);
        return {
          id: client.id,
          name: client.name,
          clientType: client.clientType,
          registrationId: client.registrationId,
          status: client.status,
          assignedLimit: tokenLimit,
          used,
          remaining: Math.max(0, tokenLimit - used),
          tokenLimit,
        };
      }),
    [clients, monthUsers]
  );

  const scopeRows = useMemo(() => {
    const rows: ScopeRow[] = [];
    const items =
      listing === 'department'
        ? quotaDirectory.departments.map((item) => ({ scope: 'department' as const, id: item.id, name: item.name }))
        : quotaDirectory.roles.map((item) => ({ scope: 'role' as const, id: item.id, name: item.name }));
    for (const item of items) {
      const memberIds = new Set(
        listing === 'department'
          ? departmentMemberIds(quotaDirectory, item.id)
          : quotaDirectory.users.filter((user) => user.roleId === item.id).map((user) => user.id)
      );
      for (const client of clients) {
        const assignment = findQuotaAssignment(state.quotaAssignments, item.scope, item.id, client.id, monthFilter);
        if (!assignment || assignment.tokenLimit <= 0) continue;
        const members = monthUsers.filter((user) => user.userId && memberIds.has(user.userId) && user.clientId === client.id);
        const used = members.reduce((sum, user) => sum + user.tokensUsed, 0);
        const perUserLimit = assignment.status === 'active' ? assignment.tokenLimit : 0;
        const tokenLimit = perUserLimit * Math.max(memberIds.size, 1);
        rows.push({
          scope: item.scope,
          scopeId: item.id,
          name: item.name,
          clientId: client.id,
          assignedLimit: assignment.tokenLimit,
          status: assignment.status,
          users: memberIds.size,
          used,
          remaining: Math.max(0, tokenLimit - used),
          tokenLimit,
        });
      }
    }
    return rows;
  }, [clients, listing, monthFilter, monthUsers, quotaDirectory, state.quotaAssignments]);

  const publishedTools = useMemo(
    () => state.published.filter((item) => capabilityOf(item) === 'tool'),
    [state.published]
  );
  const publishedToolIds = useMemo(() => new Set(publishedTools.map((item) => item.id)), [publishedTools]);
  const gatewayIds = useMemo(() => new Set(gateways.map((item) => item.id)), [gateways]);

  const scopeName = (scope: QuotaScope, scopeId: string): string => {
    if (scope === 'department') return quotaDirectory.departments.find((item) => item.id === scopeId)?.name || scopeId;
    if (scope === 'role') return quotaDirectory.roles.find((item) => item.id === scopeId)?.name || scopeId;
    return quotaDirectory.users.find((item) => item.id === scopeId)?.name || scopeId;
  };

  const visibleTools = useMemo(() => {
    const q = query.trim().toLowerCase();
    return state.quotaAssignments
      .filter((item) => {
        if (listing !== 'client' && item.scope !== listing) return false;
        if (inferQuotaResourceType(item, registeredClientIds, gatewayIds) !== 'mcp_tool') return false;
        if (!publishedToolIds.has(item.clientId)) return false;
        return findQuotaAssignment(state.quotaAssignments, item.scope, item.scopeId, item.clientId, monthFilter)?.id === item.id;
      })
      .filter((item) => {
        const tool = publishedTools.find((row) => row.id === item.clientId);
        const haystack = `${item.name} ${tool?.mcpToolName || ''} ${tool?.sourceName || ''} ${scopeName(item.scope, item.scopeId)}`.toLowerCase();
        if (utilFilter !== 'all') {
          const status = quotaStatus({ tokenLimit: item.tokenLimit, tokensUsed: 0 });
          if (status !== utilFilter) return false;
        }
        return !q || haystack.includes(q);
      })
      .map((item) => {
        const tool = publishedTools.find((row) => row.id === item.clientId);
        return {
          id: item.id,
          name: item.name || tool?.mcpToolName || tool?.sourceName || item.clientId,
          source: tool?.sourceName || scopeName(item.scope, item.scopeId),
          scopeLabel: scopeName(item.scope, item.scopeId),
          status: item.status,
          tokenLimit: item.tokenLimit,
          renewsMonthly: item.renewsMonthly,
        };
      });
  }, [
    gatewayIds,
    listing,
    monthFilter,
    publishedToolIds,
    publishedTools,
    query,
    quotaDirectory.departments,
    quotaDirectory.roles,
    quotaDirectory.users,
    registeredClientIds,
    state.quotaAssignments,
    utilFilter,
  ]);

  const visibleGateways = useMemo(() => {
    const q = query.trim().toLowerCase();
    return state.quotaAssignments
      .filter((item) => {
        if (listing !== 'client' && item.scope !== listing) return false;
        if (inferQuotaResourceType(item, registeredClientIds, gatewayIds) !== 'llm_gateway') return false;
        return findQuotaAssignment(state.quotaAssignments, item.scope, item.scopeId, item.clientId, monthFilter)?.id === item.id;
      })
      .filter((item) => {
        const gateway = gateways.find((row) => row.id === item.clientId);
        const haystack = `${item.name} ${gateway?.name || ''} ${gateway?.publicAlias || ''} ${scopeName(item.scope, item.scopeId)}`.toLowerCase();
        if (utilFilter !== 'all') {
          const status = quotaStatus({ tokenLimit: item.tokenLimit, tokensUsed: 0 });
          if (status !== utilFilter) return false;
        }
        return !q || haystack.includes(q);
      })
      .map((item) => {
        const gateway = gateways.find((row) => row.id === item.clientId);
        return {
          id: item.id,
          name: item.name || gateway?.name || item.clientId,
          publicAlias: gateway?.publicAlias || '',
          llmType: gateway?.llmType,
          status: item.status,
          tokenLimit: item.tokenLimit,
          scopeLabel: scopeName(item.scope, item.scopeId),
          renewsMonthly: item.renewsMonthly,
        };
      });
  }, [gateways, gatewayIds, listing, monthFilter, query, quotaDirectory.departments, quotaDirectory.roles, quotaDirectory.users, state.quotaAssignments, utilFilter]);

  const totals = useMemo(() => {
    if (listing === 'client') {
      return clientRows.reduce(
        (acc, row) => {
          acc.limit += row.tokenLimit;
          acc.used += row.used;
          acc.remaining += row.remaining;
          return acc;
        },
        { limit: 0, used: 0, remaining: 0 }
      );
    }
    if (listing === 'user') {
      return users.reduce(
        (acc, user) => {
          acc.limit += user.tokenLimit;
          acc.used += user.tokensUsed;
          acc.remaining += remainingTokens(user);
          return acc;
        },
        { limit: 0, used: 0, remaining: 0 }
      );
    }
    return scopeRows.reduce(
      (acc, row) => {
        acc.limit += row.tokenLimit;
        acc.used += row.used;
        acc.remaining += row.remaining;
        return acc;
      },
      { limit: 0, used: 0, remaining: 0 }
    );
  }, [clientRows, listing, scopeRows, users]);

  const visibleScopeRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return scopeRows.filter((row) => {
      const status = quotaStatus({ tokenLimit: row.tokenLimit, tokensUsed: row.used });
      if (utilFilter !== 'all' && status !== utilFilter) return false;
      if (!q) return true;
      return `${row.name} ${clientLabel(row.clientId, clients)}`.toLowerCase().includes(q);
    });
  }, [clients, query, scopeRows, utilFilter]);

  const visibleUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((user) => {
      const status = quotaStatus(user);
      if (utilFilter !== 'all' && status !== utilFilter) return false;
      if (!q) return true;
      return `${user.name} ${user.email} ${user.role} ${clientLabel(user.clientId, clients)}`.toLowerCase().includes(q);
    });
  }, [clients, query, users, utilFilter]);

  useEffect(() => {
    const stored = state.quotaAssignments.filter((item) => item.scope === 'user').length;
    logGovernedMcp('app', 'quota_listing_user_visibility', {
      month: monthFilter,
      listing,
      quotaKind,
      stored,
      assignedRows: users.length,
      visible: visibleUsers.length,
    });
  }, [listing, monthFilter, quotaKind, state.quotaAssignments, users.length, visibleUsers.length]);

  const visibleClientRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return clientRows.filter((row) => {
      const status = quotaStatus({ tokenLimit: row.tokenLimit, tokensUsed: row.used });
      if (utilFilter !== 'all' && status !== utilFilter) return false;
      if (!q) return true;
      return `${row.name} ${row.registrationId} ${clientTypeLabel(row.clientType)}`.toLowerCase().includes(q);
    });
  }, [clientRows, query, utilFilter]);

  useEffect(() => {
    setPage(1);
  }, [quotaKind, listing, query, clientFilter, monthFilter, utilFilter]);

  const listingRows =
    quotaKind === 'tool'
      ? visibleTools
      : quotaKind === 'llm'
        ? visibleGateways
        : listing === 'client'
          ? visibleClientRows
          : listing === 'user'
            ? visibleUsers
            : visibleScopeRows;
  const pagedUsers = pageSlice(visibleUsers, page);
  const pagedScopeRows = pageSlice(visibleScopeRows, page);
  const pagedClientRows = pageSlice(visibleClientRows, page);
  const pagedTools = pageSlice(visibleTools, page);
  const pagedGateways = pageSlice(visibleGateways, page);

  const summaryCards =
    quotaKind === 'tool'
      ? [
          { label: 'Assigned tools', value: String(visibleTools.length) },
          { label: `Assigned limit · ${formatQuotaMonth(monthFilter)}`, value: visibleTools.reduce((sum, row) => sum + row.tokenLimit, 0).toLocaleString() },
          { label: 'Active', value: String(visibleTools.filter((row) => row.status === 'active').length) },
        ]
      : quotaKind === 'llm'
        ? [
            { label: 'Assigned gateways', value: String(visibleGateways.length) },
            { label: `Assigned limit · ${formatQuotaMonth(monthFilter)}`, value: formatTokens(visibleGateways.reduce((sum, row) => sum + row.tokenLimit, 0)) },
            { label: 'Active', value: String(visibleGateways.filter((row) => row.status === 'active').length) },
          ]
        : [
            { label: `Assigned limit · ${formatQuotaMonth(monthFilter)}`, value: formatTokens(totals.limit) },
            { label: 'Current spend', value: formatTokens(totals.used) },
            { label: 'Remaining', value: formatTokens(totals.remaining) },
          ];

  const empty =
    quotaKind === 'tool'
      ? { title: 'No tool quotas', description: 'Assign a max tool-call quota to a role, department, or user to see it here.' }
      : quotaKind === 'llm'
        ? { title: 'No LLM gateway quotas', description: 'Assign a monthly token limit to a role, department, or user to see it here.' }
        : listing === 'client'
          ? { title: 'No clients', description: 'Register an AI client to review consumption and assign a monthly quota.' }
          : listing === 'user'
            ? { title: 'No assigned users', description: 'Assign a monthly quota to a user to see it here.' }
            : { title: 'No assignments', description: 'No departments or roles have a quota greater than 0 for this client and month.' };

  const noun =
    quotaKind === 'tool' ? 'tool' : quotaKind === 'llm' ? 'gateway' : listing === 'client' ? 'client' : listing === 'user' ? 'user' : listing;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div
          className={`inline-flex rounded-full p-1 ${isLight ? 'bg-white ring-1 ring-black/[0.04]' : 'bg-neutral-900 ring-1 ring-white/10'}`}
          role="tablist"
          aria-label="Quota type"
        >
          {QUOTA_KIND_TABS.map((tab) => {
            const Icon = tab.icon;
            const active = quotaKind === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  setQuotaKind(tab.id);
                  setPage(1);
                  setQuery('');
                  setSelectedScope(null);
                  setSelectedUser(null);
                  logGovernedMcp('app', 'quota_listing_kind', { quotaKind: tab.id });
                }}
                className={typeTabClass(active, isLight)}
              >
                <Icon size={13} />
                {tab.label}
              </button>
            );
          })}
        </div>
        <button type="button" className={`${primaryBtn()} rounded-full`} onClick={() => setAssignOpen(true)}>
          <Plus size={14} /> Assign quota
        </button>
      </div>
      <p className={`text-sm mb-4 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{selectedKind.purpose}</p>

      <div className="grid grid-cols-3 gap-3 mb-4">
        {summaryCards.map((item) => (
          <div
            key={item.label}
            className={`rounded-xl border px-4 py-3 ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'}`}
          >
            <p className={`text-[11px] uppercase tracking-wide ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{item.label}</p>
            <p className={`mt-1 text-[15px] font-semibold tabular-nums ${isLight ? 'text-neutral-900' : 'text-white'}`}>{item.value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
          <input
            className={`${fieldClass(isLight)} pl-9`}
            placeholder={
              quotaKind === 'tool'
                ? 'Search published tools…'
                : quotaKind === 'llm'
                  ? 'Search LLM gateways…'
                  : listing === 'user'
                    ? 'Search assigned users…'
                    : `Search ${listing}s…`
            }
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <select
          className={selectField(isLight)}
          value={listing === 'client' ? 'role' : listing}
          aria-label="Role, department, or user"
          onChange={(event) => {
            const next = event.target.value as Exclude<QuotaListingTab, 'client'>;
            setListing(next);
            setPage(1);
            setSelectedScope(null);
            setSelectedUser(null);
            logGovernedMcp('app', 'quota_listing_scope', { listing: next, quotaKind });
          }}
        >
          {SCOPE_FILTERS.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </select>
        <input
          type="month"
          className={selectField(isLight)}
          value={monthFilter}
          onChange={(event) => {
            logGovernedMcp('app', 'quota_month_filter', { month: event.target.value });
            setMonthFilter(event.target.value);
          }}
        />
        <select className={selectField(isLight)} value={utilFilter} onChange={(event) => setUtilFilter(event.target.value as UtilFilter)}>
          <option value="all">All utilization</option>
          <option value="ok">On track</option>
          <option value="warning">High</option>
          <option value="exceeded">Over limit</option>
        </select>
      </div>

      {listingRows.length === 0 ? (
        <EmptyState isLight={isLight} title={empty.title} description={empty.description} />
      ) : (
        <div
          className={`overflow-auto min-h-[280px] h-[calc(100vh-22rem)] rounded-2xl border shadow-sm ${
            isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-900'
          }`}
        >
          <table className="w-full min-w-[960px] text-sm">
            <thead className={tableHeadClass(isLight)}>
              {quotaKind === 'tool' ? (
                <tr>
                  <th className={thClass()}>Tool</th>
                  <th className={thClass()}>{listing === 'user' ? 'User' : listing === 'role' ? 'Role' : 'Department'}</th>
                  <th className={thClass()}>Month</th>
                  <th className={thClass()}>Status</th>
                  <th className={thClass()}>Max tool calls</th>
                </tr>
              ) : quotaKind === 'llm' ? (
                <tr>
                  <th className={thClass()}>LLM gateway</th>
                  <th className={thClass()}>{listing === 'user' ? 'User' : listing === 'role' ? 'Role' : 'Department'}</th>
                  <th className={thClass()}>Month</th>
                  <th className={thClass()}>Status</th>
                  <th className={thClass()}>Monthly token limit</th>
                </tr>
              ) : listing === 'client' ? (
                <tr>
                  <th className={thClass()}>Client</th>
                  <th className={thClass()}>Type</th>
                  <th className={thClass()}>Status</th>
                  <th className={thClass()}>Month</th>
                  <th className={thClass()}>Assigned</th>
                  <th className={thClass()}>Current spend</th>
                  <th className={thClass()}>Remaining</th>
                  <th className={thClass()}>Utilization</th>
                </tr>
              ) : listing === 'user' ? (
                <tr>
                  <th className={thClass()}> </th>
                  <th className={thClass()}>User</th>
                  <th className={thClass()}>Client</th>
                  <th className={thClass()}>Month</th>
                  <th className={thClass()}>Assigned</th>
                  <th className={thClass()}>Status</th>
                  <th className={thClass()}>Effective</th>
                  <th className={thClass()}>Source</th>
                  <th className={thClass()}>Current spend</th>
                  <th className={thClass()}>Remaining</th>
                  <th className={thClass()}>Utilization</th>
                </tr>
              ) : (
                <tr>
                  <th className={thClass()}> </th>
                  <th className={thClass()}>{listing === 'department' ? 'Department' : 'Role'}</th>
                  <th className={thClass()}>Client</th>
                  <th className={thClass()}>Month</th>
                  <th className={thClass()}>Users</th>
                  <th className={thClass()}>Assigned</th>
                  <th className={thClass()}>Status</th>
                  <th className={thClass()}>Current spend</th>
                  <th className={thClass()}>Remaining</th>
                  <th className={thClass()}>Utilization</th>
                </tr>
              )}
            </thead>
            <tbody>
              {quotaKind === 'tool'
                ? pagedTools.map((item) => (
                    <tr key={item.id} className={trClass(isLight)}>
                      <td className={tdClass()}>
                        <p className={`font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{item.name}</p>
                        {item.source ? (
                          <p className={`text-xs mt-0.5 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{item.source}</p>
                        ) : null}
                      </td>
                      <td className={tdClass()}>{item.scopeLabel}</td>
                      <td className={tdClass()}>{formatQuotaMonth(monthFilter)}</td>
                      <td className={tdClass()}>
                        <span className="inline-flex flex-wrap items-center gap-1.5">
                          <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${assignmentStatusTone(item.status, isLight)}`}>
                            {item.status === 'inactive' ? 'Inactive' : 'Active'}
                          </span>
                          {item.renewsMonthly ? (
                            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${isLight ? 'bg-sky-50 text-sky-700' : 'bg-sky-500/15 text-sky-300'}`}>
                              Renews monthly
                            </span>
                          ) : null}
                        </span>
                      </td>
                      <td className={`${tdClass()} tabular-nums`}>{item.tokenLimit.toLocaleString()}</td>
                    </tr>
                  ))
                : quotaKind === 'llm'
                  ? pagedGateways.map((item) => (
                      <tr key={item.id} className={trClass(isLight)}>
                        <td className={tdClass()}>
                          <p className={`font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{item.name}</p>
                          {item.publicAlias ? (
                            <p className={`text-xs mt-0.5 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{item.publicAlias}</p>
                          ) : null}
                        </td>
                        <td className={tdClass()}>{item.scopeLabel}</td>
                        <td className={tdClass()}>{formatQuotaMonth(monthFilter)}</td>
                        <td className={tdClass()}>
                          <span className="inline-flex flex-wrap items-center gap-1.5">
                            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${assignmentStatusTone(item.status, isLight)}`}>
                              {item.status === 'inactive' ? 'Inactive' : 'Active'}
                            </span>
                            {item.renewsMonthly ? (
                              <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${isLight ? 'bg-sky-50 text-sky-700' : 'bg-sky-500/15 text-sky-300'}`}>
                                Renews monthly
                              </span>
                            ) : null}
                          </span>
                        </td>
                        <td className={`${tdClass()} tabular-nums`}>{item.tokenLimit > 0 ? formatTokens(item.tokenLimit) : '—'}</td>
                      </tr>
                    ))
                  : listing === 'client'
                    ? pagedClientRows.map((row) => (
                        <tr key={row.id} className={trClass(isLight)}>
                          <td className={tdClass()}>
                            <div className="flex items-center gap-3 min-w-0">
                              <span
                                className={`h-9 w-9 rounded-xl text-[11px] font-semibold flex items-center justify-center shrink-0 ${
                                  isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-300'
                                }`}
                              >
                                {initials(row.name)}
                              </span>
                              <div className="min-w-0">
                                <p className={`font-medium truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>{row.name}</p>
                                <p className={`text-xs truncate ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{row.registrationId}</p>
                              </div>
                            </div>
                          </td>
                          <td className={tdClass()}>{clientTypeLabel(row.clientType)}</td>
                          <td className={tdClass()}>
                            <ClientStatusBadge status={row.status} />
                          </td>
                          <td className={tdClass()}>{formatQuotaMonth(monthFilter)}</td>
                          <td className={`${tdClass()} tabular-nums`}>
                            {row.assignedLimit > 0 ? formatTokens(row.assignedLimit) : <span className={isLight ? 'text-neutral-400' : 'text-neutral-500'}>—</span>}
                          </td>
                          <td className={`${tdClass()} tabular-nums`}>{formatTokens(row.used)}</td>
                          <td className={`${tdClass()} tabular-nums`}>{formatTokens(row.remaining)}</td>
                          <td className={tdClass()}>
                            <Utilization user={{ tokenLimit: row.tokenLimit, tokensUsed: row.used }} isLight={isLight} />
                          </td>
                        </tr>
                      ))
                    : listing === 'user'
                    ? pagedUsers.map((user) => {
                        const assigned = findQuotaAssignment(
                          state.quotaAssignments,
                          'user',
                          user.userId || '',
                          user.clientId,
                          monthFilter
                        );
                        return (
                          <tr key={user.id} className={trClass(isLight)}>
                            <td className={`${tdClass()} text-left whitespace-nowrap`}>
                              <button
                                type="button"
                                className={`${ghostBtn(isLight)} h-8 px-2.5`}
                                onClick={() => {
                                  setSelectedScope(null);
                                  setSelectedUser(user);
                                }}
                              >
                                <BarChart3 size={13} />
                                Details
                              </button>
                            </td>
                            <td className={tdClass()}>
                              <div className="flex items-center gap-3 min-w-0">
                                <span
                                  className={`h-9 w-9 rounded-xl text-[11px] font-semibold flex items-center justify-center shrink-0 ${
                                    isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-300'
                                  }`}
                                >
                                  {initials(user.name)}
                                </span>
                                <div className="min-w-0">
                                  <p className={`font-medium truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>{user.name}</p>
                                  <p className={`text-xs truncate ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                                    {user.email} · {user.role}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className={tdClass()}>{clientLabel(user.clientId, clients)}</td>
                            <td className={tdClass()}>{formatQuotaMonth(monthFilter)}</td>
                            <td className={`${tdClass()} tabular-nums`}>{assigned ? formatTokens(assigned.tokenLimit) : '—'}</td>
                            <td className={tdClass()}>
                              {assigned ? (
                                <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${assignmentStatusTone(assigned.status, isLight)}`}>
                                  {assigned.status === 'inactive' ? 'Inactive' : 'Active'}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className={`${tdClass()} tabular-nums`}>{formatTokens(user.tokenLimit)}</td>
                            <td className={tdClass()}>
                              <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${sourceTone(sourceLabel(user.source), isLight)}`}>
                                {sourceLabel(user.source)}
                              </span>
                            </td>
                            <td className={`${tdClass()} tabular-nums`}>{formatTokens(user.tokensUsed)}</td>
                            <td className={`${tdClass()} tabular-nums`}>{formatTokens(remainingTokens(user))}</td>
                            <td className={tdClass()}>
                              <Utilization user={user} isLight={isLight} />
                            </td>
                          </tr>
                        );
                      })
                    : pagedScopeRows.map((row) => {
                        const rowKey = `${row.scope}-${row.scopeId}-${row.clientId}-${monthFilter}`;
                        return (
                          <tr key={rowKey} className={trClass(isLight)}>
                            <td className={`${tdClass()} text-left whitespace-nowrap`}>
                              <button
                                type="button"
                                className={`${ghostBtn(isLight)} h-8 px-2.5`}
                                onClick={() => {
                                  setSelectedUser(null);
                                  setSelectedScope({
                                    scope: row.scope,
                                    scopeId: row.scopeId,
                                    name: row.name,
                                    clientId: row.clientId || clientFilter,
                                  });
                                }}
                              >
                                <BarChart3 size={13} />
                                Details
                              </button>
                            </td>
                            <td className={tdClass()}>
                              <p className={`font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{row.name}</p>
                            </td>
                            <td className={tdClass()}>{row.clientId ? clientLabel(row.clientId, clients) : 'Select a client'}</td>
                            <td className={tdClass()}>{formatQuotaMonth(monthFilter)}</td>
                            <td className={`${tdClass()} tabular-nums`}>{row.users}</td>
                            <td className={`${tdClass()} tabular-nums`}>
                              {row.assignedLimit == null ? '—' : formatTokens(row.assignedLimit)}
                            </td>
                            <td className={tdClass()}>
                              {row.status ? (
                                <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${assignmentStatusTone(row.status, isLight)}`}>
                                  {row.status === 'inactive' ? 'Inactive' : 'Active'}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className={`${tdClass()} tabular-nums`}>{formatTokens(row.used)}</td>
                            <td className={`${tdClass()} tabular-nums`}>{formatTokens(row.remaining)}</td>
                            <td className={tdClass()}>
                              <Utilization user={{ tokenLimit: row.tokenLimit, tokensUsed: row.used }} isLight={isLight} />
                            </td>
                          </tr>
                        );
                      })}
            </tbody>
          </table>
        </div>
      )}
      {listingRows.length > 0 ? <TablePager page={page} total={listingRows.length} isLight={isLight} onPage={setPage} noun={noun} /> : null}

      <QuotaAssignDrawer
        open={assignOpen}
        listing={listing}
        quotaKind={quotaKind}
        clientId={clientFilter}
        month={monthFilter}
        isLight={isLight}
        onClose={() => setAssignOpen(false)}
        onAssigned={({ listing: nextListing, month: nextMonth, quotaKind: nextKind }) => {
          setQuotaKind(nextKind);
          setListing(nextListing === 'client' ? 'user' : nextListing);
          if (isQuotaMonth(nextMonth)) setMonthFilter(nextMonth);
          logGovernedMcp('app', 'quota_listing_after_assign', {
            listing: nextListing,
            month: nextMonth,
            quotaKind: nextKind,
          });
        }}
        onToast={onToast}
      />
      <QuotaUserDrawer
        user={selectedUser}
        clientFilter={clientFilter}
        month={monthFilter}
        isLight={isLight}
        onClose={() => setSelectedUser(null)}
      />
      <QuotaScopeDrawer
        open={Boolean(selectedScope)}
        scope={selectedScope?.scope || 'department'}
        scopeId={selectedScope?.scopeId || ''}
        name={selectedScope?.name || ''}
        clientId={selectedScope?.clientId || clientFilter}
        month={monthFilter}
        isLight={isLight}
        onClose={() => setSelectedScope(null)}
        onOpenUser={(user) => {
          setSelectedScope(null);
          setSelectedUser(user);
        }}
      />
    </div>
  );
}

function Utilization({
  user,
  isLight,
}: {
  user: Pick<QuotaUser, 'tokenLimit' | 'tokensUsed'>;
  isLight: boolean;
}) {
  const status = quotaStatus(user);
  const percent = utilizationPercent(user);
  return (
    <div className="min-w-[140px]">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${pillTone(status, isLight)}`}>{statusLabel(status)}</span>
        <span className={`text-[11px] tabular-nums ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{percent}%</span>
      </div>
      <div className={`h-1.5 rounded-full overflow-hidden ${isLight ? 'bg-neutral-100' : 'bg-neutral-800'}`}>
        <div className={`h-full rounded-full ${barTone(status, isLight)}`} style={{ width: `${Math.min(100, percent)}%` }} />
      </div>
    </div>
  );
}
